import { useInput, useRouting } from '@/hooks'
import { memo, useEffect, useState } from 'react'
import { useCompanyContext } from '../../CompanyDetails.context'
import { Alert, Api } from '@/modules'
import { Nbr } from '@/utils/numbers'
import { UpdatePublisherRequest } from '@shared/models'
import { Button, Flex, Form, Input } from '@/components'
import { handleWeightChange } from '../../utils'
import { DialogContainer, DialogOverlay, ModalHeaderContainer } from './EditPublisher.styles'
import { Close } from '@icon-park/react'
import { Tokens } from '@/assets/tokens'

const MIN_WEIGHT = 1
const MAX_WEIGHT = 5

export const EditPublisherModal = memo(Component)

function Component(): JSX.Element | null {
  const { currentPage, companyID, queryParams, changeQueryParam } = useRouting()
  const { publishers, reloadPublishers } = useCompanyContext()

  const [editPublisherID, setEditPublisherID] = useState<string | null>(null)

  const { sendAlert } = Alert.Hook()

  const { isRunning: isUpdatingPublisher, run: updatePublisher } = Api.Command<void>(
    Api.Methods.PATCH,
    'companies/:companyID/publishers/:publisherID',
  )

  const { binder: nameBinder, reset: resetName } = useInput()
  const { value: weight, binder: weightBinder, reset: resetWeight } = useInput()
  const { value: minFollowers, binder: minFollowersBinder, reset: resetMinFollowers } = useInput()
  const { value: maxFollowers, binder: maxFollowersBinder, reset: resetMaxFollowers } = useInput()

  useEffect(() => {
    const newEditId = queryParams.editPublisherId || null
    setEditPublisherID(newEditId)
  }, [queryParams.editPublisherId])

  useEffect(() => {
    if (!editPublisherID || !publishers) return

    const publisher = publishers.elements.find((p) => p.id === editPublisherID)

    if (!publisher) return

    resetName(publisher.name)
    resetWeight(String(publisher.weight))
    resetMinFollowers(String(publisher.minFollowers))
    resetMaxFollowers(String(publisher.maxFollowers))
  }, [editPublisherID, publishers])

  function handleClose(): void {
    changeQueryParam({ param: 'editPublisherId', value: undefined })
  }

  function onSubmit(): void {
    const weightNbr = Nbr.from(weight)
    const minFollowersNbr = Nbr.from(minFollowers)
    const maxFollowersNbr = Nbr.from(maxFollowers)

    if (!editPublisherID) return

    const originalPublisher = publishers?.elements.find((p) => p.id === editPublisherID)
    if (!originalPublisher) return

    const input: UpdatePublisherRequest = {}

    if (weightNbr.value !== originalPublisher.weight) {
      input.weight = weightNbr.value
    }

    if (!minFollowersNbr.isBlank && minFollowersNbr.value !== originalPublisher.minFollowers) {
      input.minFollowers = minFollowersNbr.value!
    }

    if (!maxFollowersNbr.isBlank && maxFollowersNbr.value !== originalPublisher.maxFollowers) {
      input.maxFollowers = maxFollowersNbr.value!
    }

    if (Object.keys(input).length > 0) {
      updatePublisher({
        binds: [
          ['companyID', companyID!],
          ['publisherID', editPublisherID],
        ],
        input,
        onSuccess: () => {
          handleClose()
          reloadPublishers({ page: currentPage })
          sendAlert({
            type: 'success',
            title: 'Sucesso',
            message: 'Publicador atualizado com sucesso',
          })
        },
      })
    } else {
      handleClose()
    }
  }

  if (!editPublisherID) {
    return null
  }

  return (
    <DialogOverlay>
      <DialogContainer>
        <ModalHeaderContainer $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
          <h1>Atualizar Publicador</h1>
          <Button.Root $variant="link" onClick={handleClose}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeaderContainer>
        <Form onSubmit={onSubmit} controlParam={isUpdatingPublisher}>
          <Flex $type="column" $align="stretch" $gap={24} $fit>
            <Input label="Nome" placeholder="Nome" {...nameBinder} disabled={true} />
            <Flex $type="row" $justify="space-between" $align="center" $gap={24} $fit>
              <Input
                label="Peso"
                type="number"
                placeholder="Peso"
                {...weightBinder}
                required
                onChange={(value: string) => handleWeightChange(weightBinder, MIN_WEIGHT, MAX_WEIGHT, value)}
              />
              <Input
                label="Min. #seguidores"
                type="number"
                maxLength={16}
                min={0}
                placeholder="Min. #seguidores"
                {...minFollowersBinder}
              />
              <Input
                label="Max. #seguidores"
                type="number"
                maxLength={16}
                min={0}
                placeholder="Max. #seguidores"
                {...maxFollowersBinder}
              />
            </Flex>
            <Flex $type="row" $justify="flex-end" $align="stretch" $gap={20} $fit>
              <Button.Root
                type="button"
                $size={114}
                onClick={handleClose}
                disabled={isUpdatingPublisher}
                $loading={isUpdatingPublisher}>
                <Button.Text value="Cancelar" />
              </Button.Root>
              <Button.Root
                type="submit"
                $size={114}
                $variant="primary"
                disabled={isUpdatingPublisher}
                $loading={isUpdatingPublisher}>
                <Button.Text value="Salvar" />
              </Button.Root>
            </Flex>
          </Flex>
        </Form>
      </DialogContainer>
    </DialogOverlay>
  )
}
